// Copyright (c) Microsoft. All rights reserved.

import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { TokenUsage } from '../../../libs/models/TokenUsage';
import {
    ActiveUserInfo,
    Alert,
    AlertMetadata,
    AppState,
    FeatureKeys,
    IConversationDisplayMode,
    initialState,
} from './AppState';
import { IArchiveSourceAvailability } from '../../../libs/models/ArchiveSourceAvailability';

export const appSlice = createSlice({
    name: 'app',
    initialState,
    reducers: {
        setAlerts: (state: AppState, action: PayloadAction<Alert[]>) => {
            state.alerts = action.payload;
        },
        addAlert: (state: AppState, action: PayloadAction<Alert>) => {
            if (state.alerts.length === 3) {
                state.alerts.shift();
            }
            state.alerts.push(action.payload);
        },
        removeAlert: (state: AppState, action: PayloadAction<number>) => {
            state.alerts.splice(action.payload, 1);
        },
        removeAlertsWithMetadata: (state: AppState, action: PayloadAction<AlertMetadata>) => {
            state.alerts = state.alerts.filter((alert) => alert.metadata !== action.payload);
        },
        setActiveUserInfo: (state: AppState, action: PayloadAction<ActiveUserInfo>) => {
            state.activeUserInfo = action.payload;
        },
        updateTokenUsage: (state: AppState, action: PayloadAction<TokenUsage>) => {
            Object.entries(action.payload).forEach(([key, value]) => {
                action.payload[key] = getTotalTokenUsage(state.tokenUsage[key], value);
            });
            state.tokenUsage = action.payload;
        },
        updateAppErrorState: (state: AppState, action: PayloadAction<boolean>) => {
            state.appErrorState = action.payload;
        },
        updateInputDisabledState: (state: AppState, action: PayloadAction<boolean>) => {
            state.isInputDisabled = action.payload;
        },
        // This sets the feature flag based on end user input
        toggleFeatureFlag: (state: AppState, action: PayloadAction<FeatureKeys>) => {
            const feature = state.features[action.payload];
            state.features = {
                ...state.features,
                [action.payload]: {
                    ...feature,
                    enabled: !feature.enabled,
                },
            };
        },
        // This controls feature availability based on the state of backend
        toggleFeatureState: (
            state: AppState,
            action: PayloadAction<{
                feature: FeatureKeys;
                deactivate: boolean;
                enable: boolean;
            }>,
        ) => {
            const feature = state.features[action.payload.feature];
            state.features = {
                ...state.features,
                [action.payload.feature]: {
                    ...feature,
                    enabled: action.payload.deactivate ? false : action.payload.enable,
                    inactive: action.payload.deactivate,
                },
            };
        },
        setConversationDisplayMode: (state: AppState, action: PayloadAction<IConversationDisplayMode>) => {
            state.conversationDisplayMode = action.payload;
        },
        setArchiveSourceAvailability: (state: AppState, action: PayloadAction<IArchiveSourceAvailability>) => {
            state.archiveSourceAvailability = action.payload;
        },
    },
});

export const {
    addAlert,
    removeAlert,
    removeAlertsWithMetadata,
    setAlerts,
    setActiveUserInfo,
    toggleFeatureFlag,
    toggleFeatureState,
    updateTokenUsage,
    setConversationDisplayMode,
    setArchiveSourceAvailability,
    updateAppErrorState,
    updateInputDisabledState,
} = appSlice.actions;

export default appSlice.reducer;

const getTotalTokenUsage = (previousSum?: number, current?: number) => {
    if (previousSum === undefined) {
        return current;
    }
    if (current === undefined) {
        return previousSum;
    }

    return previousSum + current;
};
